import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  isMantraAdmin,
  isMcpUser,
  useCurrentProvider,
} from '../../../../../../Components/Permissions';
import {
  CareStatus,
  PaymentSource,
  useEditUserCareFlowsMutation,
  UserCareFlowInput,
} from '../../../../../../graphQL';
import { useDrilldownContext } from '../../../../helpers';
import { useGeoStateAvailability } from '../../../../../../Hooks/useGeoStateAvailability';
import { getRemainingSessions } from '../../../../../../utils';
import { useForm } from 'react-hook-form';
import {
  CareFlowErrorNotification,
  CareFlowMap,
  EditCareFlowForm,
  OrgCareFlowMap,
  SessionLimitsMap,
} from '../../types';
import { isStudentCareFlowEditable, NONE_OPTION_ID } from '../../utils';
import { useGetEditPatientInfo } from '../fetch/useGetEditPatientInfo';
import { useCareTypeConfigContext } from '../../../../../../Components/CareTypeConfigContext';

type PatientCareFlowModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

const MCP_REMOVE_ALL_CARETYPES_ERROR = 'MCP cannot remove all caretypes';
const MCP_REMOVE_ALL_MESSAGE = 'Patient must have at least 1 active care plan.';
const MCP_REMOVE_ALL_CONTACT = " To fully deactivate a patient's Mantra care, please ";
const CARE_TYPE_CONFLICT_CONTACT =
  ' If you would like to add an additional care type for this user, please ';

export const usePatientCareFlowModal = ({ isOpen, onClose }: PatientCareFlowModalProps) => {
  const { currentProvider } = useCurrentProvider();
  const [saving, setSaving] = useState(false);
  const { user, refetch } = useDrilldownContext();
  const { validateStateCareTypes } = useGeoStateAvailability(user.organization?.id || 0);
  const {
    data,
    providers,
    providerAssignmentMap,
    loading: loadingPatientInfo,
  } = useGetEditPatientInfo();
  const [editUserCareFlows] = useEditUserCareFlowsMutation();
  // The view model owns the server error end-to-end: it is captured when the save fails
  // and cleared when the user edits a field, resubmits, or reopens the modal. (Apollo 3.4
  // has no mutation reset() and RHF 6 has no watch subscription, so we manage it locally.)
  const [serverError, setServerError] = useState<Error | undefined>(undefined);
  const submittedValuesRef = useRef<string | undefined>(undefined);
  const { careTypeDisplayConfigs, getServiceTitle } = useCareTypeConfigContext();
  const form = useForm<EditCareFlowForm>();

  const careTypeToOrgFlow = useMemo(
    () =>
      (user.organization?.careFlows ?? []).reduce(
        (acc, flow) => ({ ...acc, [flow.careType]: flow }),
        {} as OrgCareFlowMap
      ),
    [user.organization?.careFlows]
  );

  const careFlowMap = useMemo(
    () =>
      user.careFlows.reduce((acc, flow) => ({ ...acc, [flow.careType]: flow }), {} as CareFlowMap),
    [user.careFlows]
  );

  const sessionLimitsMap = useMemo(
    () =>
      user.careFlows.reduce(
        (acc, flow) => ({ ...acc, [flow.careType]: getRemainingSessions(flow) }),
        {} as SessionLimitsMap
      ),
    [user.careFlows]
  );

  // Reset form when the modal opens or when initial data finishes loading.
  // Using isOpen ensures stale values are replaced after save-then-reopen.
  useEffect(() => {
    if (isOpen && !loadingPatientInfo) {
      const defaults = Object.fromEntries(
        careTypeDisplayConfigs.map(({ careType }) => {
          const flow = careFlowMap[careType];
          const limits = sessionLimitsMap[careType];

          if (!flow) return [careType, undefined];

          return [
            careType,
            {
              careStatus: flow.careStatus,
              paymentSource: flow.paymentSource,
              providerId: providerAssignmentMap[careType],
              remainingSessions: limits?.remaining,
            },
          ];
        })
      ) as EditCareFlowForm;
      form.reset(defaults);
      setServerError(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, loadingPatientInfo]);

  const validateCareTypes = useMemo(
    () =>
      validateStateCareTypes && user.primaryAddressState
        ? validateStateCareTypes(user.primaryAddressState)
        : undefined,
    [validateStateCareTypes, user.primaryAddressState]
  );

  const handleFormSubmit = useCallback(
    async (values: EditCareFlowForm) => {
      setSaving(true);
      setServerError(undefined);
      const fallbackPaymentSource =
        user.origin === 'Dtc' ? PaymentSource.SelfPay : PaymentSource.Organization;

      const careFlowsToSubmit: UserCareFlowInput[] = [];
      for (const { careType } of careTypeDisplayConfigs) {
        const careFlowValues = values[careType];
        if (!careFlowValues?.careStatus) continue;

        const existingFlow = careFlowMap[careType];
        const limits = sessionLimitsMap[careType];
        const remainingInput =
          careFlowValues.remainingSessions !== undefined &&
          careFlowValues.remainingSessions !== null
            ? Number(careFlowValues.remainingSessions)
            : undefined;
        const paymentSource =
          careFlowValues.paymentSource ?? existingFlow?.paymentSource ?? fallbackPaymentSource;

        const previousProviderId = providerAssignmentMap[careType];
        const newProviderId =
          careFlowValues.providerId === NONE_OPTION_ID ? null : careFlowValues.providerId ?? null;
        const hasProviderIdChanged =
          careFlowValues.providerId !== undefined && newProviderId !== (previousProviderId ?? null);

        careFlowsToSubmit.push({
          careType,
          careStatus: careFlowValues.careStatus,
          paymentSource: isMcpUser(currentProvider)
            ? careTypeToOrgFlow[careType]?.defaultPaymentSource ?? paymentSource
            : paymentSource,
          sessionLimitOverride:
            remainingInput !== undefined && limits && remainingInput !== limits.remaining
              ? remainingInput + limits.sessionsUsed
              : undefined,
          providerId: hasProviderIdChanged ? newProviderId : undefined,
        });
      }

      try {
        await editUserCareFlows({
          variables: {
            editUserCareFlows: {
              userId: user.id,
              careFlows: careFlowsToSubmit,
            },
          },
        });
        await refetch();
        onClose();
      } catch (err) {
        submittedValuesRef.current = JSON.stringify(values);
        setServerError(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setSaving(false);
      }
    },
    [
      user.id,
      user.origin,
      currentProvider,
      careTypeDisplayConfigs,
      careFlowMap,
      sessionLimitsMap,
      careTypeToOrgFlow,
      providerAssignmentMap,
      editUserCareFlows,
      refetch,
      onClose,
    ]
  );

  const onSubmit = form.handleSubmit(handleFormSubmit);

  const visibleCareTypes = useMemo(
    () =>
      careTypeDisplayConfigs.filter(
        ({ careType }) => isMantraAdmin(currentProvider) || !!careTypeToOrgFlow[careType]
      ),
    [careTypeDisplayConfigs, currentProvider, careTypeToOrgFlow]
  );

  const formValues = form.watch();

  // Clear the server error as soon as the user edits a field
  useEffect(() => {
    if (serverError && JSON.stringify(formValues) !== submittedValuesRef.current) {
      setServerError(undefined);
    }
  }, [serverError, formValues]);

  const activeCareTypes = useMemo(
    () =>
      new Set(
        careTypeDisplayConfigs
          .filter(({ careType }) => formValues[careType]?.careStatus === CareStatus.Active)
          .map(({ careType }) => careType)
      ),
    [careTypeDisplayConfigs, formValues]
  );

  const careTypeConflict = useMemo(() => {
    for (const config of careTypeDisplayConfigs) {
      if (!activeCareTypes.has(config.careType)) continue;
      const conflictingCareType = config.exclusivityGroupCareTypes.find(careType =>
        activeCareTypes.has(careType)
      );
      if (conflictingCareType) {
        return {
          serviceTitles: [getServiceTitle(config.careType), getServiceTitle(conflictingCareType)],
        };
      }
    }
    return undefined;
  }, [careTypeDisplayConfigs, activeCareTypes, getServiceTitle]);

  const error = useMemo<CareFlowErrorNotification | undefined>(() => {
    if (careTypeConflict) {
      const [first, second] = careTypeConflict.serviceTitles;
      const base = `${first} and ${second} can not be assigned simultaneously.`;
      return isMcpUser(currentProvider)
        ? { message: `${base}${CARE_TYPE_CONFLICT_CONTACT}`, showContactLink: true }
        : { message: base, showContactLink: false };
    }
    if (serverError) {
      if (serverError.message === MCP_REMOVE_ALL_CARETYPES_ERROR) {
        return isMcpUser(currentProvider)
          ? { message: `${MCP_REMOVE_ALL_MESSAGE}${MCP_REMOVE_ALL_CONTACT}`, showContactLink: true }
          : { message: MCP_REMOVE_ALL_MESSAGE, showContactLink: false };
      }
      return { message: serverError.message, showContactLink: false };
    }
    return undefined;
  }, [careTypeConflict, currentProvider, serverError]);

  const disableSubmit =
    Boolean(careTypeConflict) ||
    visibleCareTypes.some(
      ({ careType }) =>
        !isStudentCareFlowEditable(currentProvider.role, formValues[careType]?.careStatus)
    );

  return {
    error,
    data,
    form,
    saving,
    visibleCareTypes,
    providers,
    sessionLimitsMap,
    careTypeConflict,
    disableSubmit,
    onSubmit,
    validateCareTypes,
  };
};
